import { AddInError } from '@utils-global';

/**
 * Handles authentication errors from Dialog API fallback authentication.
 * Checks for specific error patterns and shows appropriate user messages.
 *
 * @param messageFromDialog - The parsed message from the authentication dialog
 * @param showToast - Function to show toast notifications
 * @throws {AddInError} For unexpected authentication errors
 */
export const handleDialogAuthError = (
  messageFromDialog: { status: string; result?: string },
  showToast: (translationKey: string, options?: any) => void,
): void => {
  const { status } = messageFromDialog;
  const result = messageFromDialog.result || '';

  // Handle admin consent required status
  if (status === 'admin_consent_required') {
    showToast('errors.adminConsentRequired');
    return;
  }

  // Handle failure status with specific error checking
  if (status === 'failure') {
    // User explicitly declined consent - expected behavior
    if (result.includes('AADSTS65004') || result.includes('User declined to consent')) {
      showToast('errors.fallback.consentDeclined');
      return;
    }

    // User canceled the auth flow - expected behavior
    if (result.includes('user_canceled') || result.includes('User cancelled')) {
      showToast('errors.fallback.userCanceled');
      return;
    }

    // Generic access denied - could be policy, permissions, etc.
    if (result.includes('access_denied')) {
      showToast('errors.fallback.accessDenied');
      return;
    }

    // Truly unexpected failure - throw to be logged to Sentry
    throw new AddInError(`Unexpected auth error happened: ${JSON.stringify(messageFromDialog)}`);
  }

  // Unknown status - throw to be logged to Sentry
  throw new AddInError(`Unexpected auth status: ${status}`);
};
